import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusChip } from "@/components/common/StatusChip";
import { MapCard } from "@/components/map/MapCard";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api, { API_BASE } from "@/lib/api";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useSocket } from "@/context/SocketContext";
import { MessageCircle, Star, DollarSign, MapPin, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { InlineNotice } from "@/components/common/InlineNotice";
import RatingModal from "@/components/ratings/RatingModal";
import { Skeleton } from "@/components/ui/skeleton";
import { BadgeList } from "@/components/common/Badges";

export default function BookingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const socket = useSocket();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nearby, setNearby] = useState([]);
  const [clientId, setClientId] = useState(null);
  const [rateOpen, setRateOpen] = useState(false);

  async function load() {
    try {
      const { data } = await api.get(`/bookings/${id}`);
      setBooking(data.booking);
      if (data.booking.status === "pending" && user?.role === "athlete") {
        const nb = await api.get(`/bookings/${id}/nearby-ballboys`);
        setNearby(nb.data.items || []);
      }
    } catch (e) {
      toast.error("Booking not found");
      nav("/bookings");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    axios.get(`${API_BASE}/public/paypal-client-id`).then((r) => setClientId(r.data.client_id)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!socket) return;
    const off = socket.on((msg) => {
      if (!msg) return;
      if (["booking_accepted", "booking_status", "payment_confirmed", "payment_received"].includes(msg.event)) {
        if (msg.data?.booking_id === id) load();
      }
    });
    return off;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, id]);

  const isAthlete = user?.role === "athlete";
  const isBallboy = user?.role === "ballboy";
  const canCancel = booking && ["pending", "accepted"].includes(booking.status);
  const canComplete = booking && booking.status === "arrived" && isBallboy;
  const canPay = booking && booking.status === "completed" && booking.payment_status !== "paid" && isAthlete;
  const canRate = useMemo(() => {
    if (!booking || booking.status !== "completed") return false;
    if (isAthlete && !booking.athlete_rated) return true;
    if (isBallboy && !booking.ballboy_rated) return true;
    return false;
  }, [booking, isAthlete, isBallboy]);

  async function doStatus(status, eta) {
    try {
      await api.post(`/bookings/${id}/status`, { status, eta_minutes: eta });
      toast.success(`Updated to ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }

  async function cancel() {
    await doStatus("cancelled");
  }

  if (loading) return <div className="space-y-3"><Skeleton className="h-24" /><Skeleton className="h-60" /></div>;
  if (!booking) return null;

  const counterpart = isAthlete ? booking.ballboy : booking.athlete;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <StatusChip status={booking.status} />
            <div className="text-xs text-muted-foreground">
              {new Date(booking.start_at).toLocaleString()}
            </div>
          </div>
          <div className="text-xl font-semibold capitalize bb-display">{booking.sport} session</div>
          <div className="text-sm text-muted-foreground flex items-center gap-1">
            <MapPin className="h-4 w-4" /> {booking.address || `${booking.lat.toFixed(4)}, ${booking.lng.toFixed(4)}`}
          </div>
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <span className="tabular-nums">{booking.duration_hours}h · ${booking.hourly_rate}/hr</span>
            </div>
            <div className="text-xl bb-display font-semibold tabular-nums">${booking.price_total?.toFixed?.(2)}</div>
          </div>
          {booking.eta_minutes != null && booking.status === "en_route" && (
            <div className="text-sm text-[hsl(var(--info))]">
              ETA: <span className="font-semibold tabular-nums" data-testid="active-booking-eta">{booking.eta_minutes} min</span>
            </div>
          )}
        </CardContent>
      </Card>

      <MapCard
        center={[booking.lat, booking.lng]}
        pins={[{ lat: booking.lat, lng: booking.lng, variant: "athlete", label: booking.address }]}
        radiusMeters={500}
        height={220}
        testId="booking-map"
      />

      {counterpart && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Your match</CardTitle></CardHeader>
          <CardContent className="pt-0 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full overflow-hidden bg-primary/10 grid place-items-center text-primary font-semibold bb-display">
                  {counterpart.photo ? (
                    <img src={counterpart.photo} alt="" className="h-full w-full object-cover" />
                  ) : counterpart.name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <div className="font-medium">{counterpart.name}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <Star className="h-3 w-3" /> {counterpart.rating_avg?.toFixed?.(1) || "0.0"} ({counterpart.rating_count || 0})
                  </div>
                </div>
              </div>
              <Link to={`/messages/${booking.id}`}>
                <Button size="sm" variant="secondary" data-testid="active-booking-open-chat">
                  <MessageCircle className="h-4 w-4 mr-1" /> Chat
                </Button>
              </Link>
            </div>
            {counterpart.badges?.length > 0 && <BadgeList badges={counterpart.badges} max={6} />}
          </CardContent>
        </Card>
      )}

      {/* Athlete actions */}
      {isAthlete && booking.status === "pending" && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Nearby ball boys</CardTitle></CardHeader>
          <CardContent className="space-y-2 pt-0">
            {nearby.length === 0 ? (
              <div className="text-sm text-muted-foreground">Searching… you'll be notified when a ball boy accepts.</div>
            ) : (
              nearby.map((b) => (
                <div key={b.id} className="rounded-xl border border-border p-3" data-testid="nearby-ballboy-card">
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-10 w-10 rounded-full overflow-hidden bg-primary/10 text-primary grid place-items-center font-semibold bb-display shrink-0">
                        {b.photo ? <img src={b.photo} alt="" className="h-full w-full object-cover" /> : b.name?.[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {b.name}
                          {(() => {
                            const lvl = b.sport_experience?.[booking.sport] || b.experience_level;
                            const map = { never: "Never played", recreational: "Recreational", high_school: "High school", college: "College", professional: "Pro", coach: "Coach" };
                            return lvl && map[lvl] ? (
                              <span className="ml-2 text-[10px] uppercase tracking-wider text-primary font-semibold">
                                {map[lvl]} {booking.sport}
                              </span>
                            ) : null;
                          })()}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          ★ {(b.rating_avg || 0).toFixed(1)} ({b.rating_count || 0}) · ${b.hourly_rate}/hr
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                          {b.distance_miles != null && (
                            <span data-testid="nearby-distance">📍 {b.distance_miles < 0.1 ? "< 0.1" : b.distance_miles.toFixed(2)} mi</span>
                          )}
                          {b.eta_minutes != null && (
                            <span data-testid="nearby-eta">⏱ {b.eta_minutes} min</span>
                          )}
                        </div>
                        {b.bio && <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{b.bio}</div>}
                      </div>
                    </div>
                    <StatusChip status="pending" />
                  </div>
                  {b.badges?.length > 0 && (
                    <div className="mt-2"><BadgeList badges={b.badges} max={5} /></div>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {isBallboy && booking.status === "accepted" && (
        <div className="grid grid-cols-2 gap-2">
          <Button onClick={() => {
            const eta = parseInt(prompt("Estimated minutes to arrive?", "10") || "10", 10);
            doStatus("en_route", isNaN(eta) ? 10 : eta);
          }} data-testid="ballboy-en-route-button"><RefreshCw className="h-4 w-4 mr-1" /> En route</Button>
          <Button variant="secondary" onClick={() => doStatus("arrived")} data-testid="ballboy-arrived-button">Mark arrived</Button>
        </div>
      )}
      {isBallboy && booking.status === "en_route" && (
        <Button className="w-full" variant="secondary" onClick={() => doStatus("arrived")} data-testid="ballboy-arrived-button">Mark arrived</Button>
      )}
      {canComplete && (
        <Button className="w-full" onClick={() => doStatus("completed")} data-testid="ballboy-complete-button">Mark completed</Button>
      )}

      {canPay && (
        <Card data-testid="paypal-checkout-container">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><DollarSign className="h-4 w-4" /> Pay via PayPal</CardTitle></CardHeader>
          <CardContent>
            {!clientId ? (
              <Skeleton className="h-12 w-full" />
            ) : (
              <PayPalScriptProvider options={{ "client-id": clientId, currency: "USD", intent: "capture" }}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "blue", shape: "rect", label: "pay" }}
                  createOrder={async () => {
                    const { data } = await api.post(`/bookings/${id}/payment/order`);
                    return data.order_id;
                  }}
                  onApprove={async (data) => {
                    await api.post(`/bookings/${id}/payment/capture`, { order_id: data.orderID });
                    toast.success("Payment successful!");
                    await load();
                  }}
                  onError={(err) => toast.error("PayPal error: " + err)}
                />
              </PayPalScriptProvider>
            )}
          </CardContent>
        </Card>
      )}

      {booking.payment_status === "paid" && (
        <InlineNotice tone="success" title="Payment received">
          Thanks! Your session was paid.
        </InlineNotice>
      )}

      {canRate && (
        <Button variant="secondary" className="w-full" onClick={() => setRateOpen(true)} data-testid="rating-open-modal">
          <Star className="h-4 w-4 mr-1" /> Rate {counterpart?.name}
        </Button>
      )}
      <RatingModal open={rateOpen} onOpenChange={setRateOpen} bookingId={id} onDone={load} />

      {canCancel && (
        <Button variant="ghost" className="w-full text-destructive" onClick={cancel} data-testid="booking-cancel-button">
          <X className="h-4 w-4 mr-1" /> Cancel booking
        </Button>
      )}
    </div>
  );
}
